import { Injectable } from '@angular/core';
import { GridCell, WordPosition } from '@meta-press/shared-plugins';

/**
 * Service for generating Word Maze grids
 * Implements the snake-path algorithm for placing words in a grid
 */
@Injectable({
	providedIn: 'root',
})
export class WordMazeGridGeneratorService {
	// Letters to use for filling empty cells (including Finnish/Swedish special characters)
	private readonly FILL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÅ';

	/**
	 * Generate a grid with the given words placed using snake-like paths
	 * @param words Array of words to place in the grid (3-9 letters each)
	 * @param gridSize Size of the square grid (10, 12, or 15)
	 * @returns Object containing the grid and word positions
	 */
	public generateGrid(
		words: string[],
		gridSize: number,
	): {
		grid: string[][];
		wordPositions: WordPosition[];
	} {
		// Initialize empty grid
		const grid: string[][] = Array(gridSize)
			.fill(null)
			.map(() => Array(gridSize).fill(''));

		const wordPositions: WordPosition[] = [];

		// Normalize and filter words
		const normalizedWords = words
			.map(w => w.toUpperCase().trim())
			.filter(w => w.length >= 3 && w.length <= 12);

		// Shuffle words to randomize placement order
		const shuffledWords = this.shuffleArray([...normalizedWords]);

		// Try to place each word
		shuffledWords.forEach((word, index) => {
			let placed = false;
			let attempts = 0;
			const maxAttempts = 200;

			while (!placed && attempts < maxAttempts) {
				attempts++;

				// Try random starting position
				const startRow = Math.floor(Math.random() * gridSize);
				const startCol = Math.floor(Math.random() * gridSize);

				const path = this.findWordPath(
					word,
					startRow,
					startCol,
					grid,
					gridSize,
				);

				if (path) {
					const wordPos = this.placeWordOnPath(word, path, grid, index % 15);
					wordPositions.push(wordPos);
					placed = true;
				}
			}

			if (!placed) {
				console.warn(`Could not place word: ${word} after ${maxAttempts} attempts`);
			}
		});

		// Fill empty cells with random letters
		this.fillEmptyCells(grid, gridSize);

		return { grid, wordPositions };
	}

	/**
	 * Find a snake-like path for placing a word
	 * @returns Array of GridCells forming the path, or null if no path found
	 */
	private findWordPath(
		word: string,
		startRow: number,
		startCol: number,
		grid: string[][],
		gridSize: number,
	): GridCell[] | null {
		// Check if starting position is available (must be empty - no cell reuse allowed)
		const startCell = grid[startRow][startCol];
		if (startCell !== '') {
			return null;
		}

		const path: GridCell[] = [
			{ letter: word[0], row: startRow, col: startCol },
		];
		const used = new Set<string>([`${startRow},${startCol}`]);

		// Try to build a path for the rest of the word
		for (let i = 1; i < word.length; i++) {
			const lastPos = path[path.length - 1];
			const nextPositions = this.getAdjacentPositions(
				lastPos.row,
				lastPos.col,
				gridSize,
				used,
			);

			// Shuffle to create snake-like randomness
			this.shuffleArray(nextPositions);

			let found = false;
			for (const pos of nextPositions) {
				const cellValue = grid[pos.row][pos.col];
				// Only allow empty cells - no cell reuse between different words
				if (cellValue === '') {
					path.push({ letter: word[i], row: pos.row, col: pos.col });
					used.add(`${pos.row},${pos.col}`);
					found = true;
					break;
				}
			}

			if (!found) {
				return null; // Can't complete the path
			}
		}

		return path;
	}

	/**
	 * Get all adjacent positions (8 directions including diagonals)
	 */
	private getAdjacentPositions(
		row: number,
		col: number,
		gridSize: number,
		used: Set<string>,
	): GridCell[] {
		const positions: GridCell[] = [];
		const directions = [
			[-1, -1],
			[-1, 0],
			[-1, 1],
			[0, -1],
			[0, 1],
			[1, -1],
			[1, 0],
			[1, 1],
		];

		for (const [dr, dc] of directions) {
			const newRow = row + dr;
			const newCol = col + dc;
			const key = `${newRow},${newCol}`;

			if (
				newRow >= 0 &&
				newRow < gridSize &&
				newCol >= 0 &&
				newCol < gridSize &&
				!used.has(key)
			) {
				positions.push({ letter: '', row: newRow, col: newCol });
			}
		}

		return positions;
	}

	/**
	 * Place a word on the grid following the given path
	 */
	private placeWordOnPath(
		word: string,
		path: GridCell[],
		grid: string[][],
		colorIndex: number,
	): WordPosition {
		const positions: GridCell[] = [];

		for (let i = 0; i < word.length; i++) {
			const pos = path[i];
			grid[pos.row][pos.col] = word[i];
			positions.push({
				letter: word[i],
				row: pos.row,
				col: pos.col,
			});
		}

		return {
			word: word,
			positions: positions,
			found: false,
			colorIndex: colorIndex,
		};
	}

	/**
	 * Fill all empty cells with random letters
	 */
	private fillEmptyCells(grid: string[][], gridSize: number): void {
		for (let i = 0; i < gridSize; i++) {
			for (let j = 0; j < gridSize; j++) {
				if (grid[i][j] === '') {
					const randomIndex = Math.floor(
						Math.random() * this.FILL_LETTERS.length,
					);
					grid[i][j] = this.FILL_LETTERS[randomIndex];
				}
			}
		}
	}

	/**
	 * Shuffle an array in place (Fisher-Yates algorithm)
	 */
	private shuffleArray<T>(array: T[]): T[] {
		for (let i = array.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[array[i], array[j]] = [array[j], array[i]];
		}
		return array;
	}

	/**
	 * Validate that words are appropriate for the grid size
	 */
	public validateWords(
		words: string[],
		gridSize: number,
	): { valid: boolean; error?: string } {
		if (!words || words.length === 0) {
			return { valid: false, error: 'No words provided' };
		}

		if (words.length > gridSize * 2) {
			return {
				valid: false,
				error: `Too many words for ${gridSize}x${gridSize} grid`,
			};
		}

		for (const word of words) {
			const trimmed = word.trim();
			if (trimmed.length < 3) {
				return { valid: false, error: `Word "${word}" is too short (min 3 letters)` };
			}
			if (trimmed.length > 12) {
				return { valid: false, error: `Word "${word}" is too long (max 12 letters)` };
			}
			if (trimmed.length > gridSize) {
				return {
					valid: false,
					error: `Word "${word}" is longer than grid size`,
				};
			}
		}

		return { valid: true };
	}

	/**
	 * Check if any words share cell positions (overlapping cells)
	 * Returns information about overlapping cells if found
	 */
	public hasOverlappingCells(wordPositions: WordPosition[]): {
		hasOverlap: boolean;
		overlappingWords?: { word1: string; word2: string; cell: string }[];
	} {
		const cellToWords = new Map<string, string[]>();

		// Build a map of cell positions to words that use them
		for (const wordPos of wordPositions) {
			for (const pos of wordPos.positions) {
				const key = `${pos.row},${pos.col}`;
				if (!cellToWords.has(key)) {
					cellToWords.set(key, []);
				}
				cellToWords.get(key)!.push(wordPos.word);
			}
		}

		// Find cells used by multiple words
		const overlaps: { word1: string; word2: string; cell: string }[] = [];
		cellToWords.forEach((words, cell) => {
			if (words.length > 1) {
				// This cell is shared by multiple words
				for (let i = 0; i < words.length; i++) {
					for (let j = i + 1; j < words.length; j++) {
						overlaps.push({
							word1: words[i],
							word2: words[j],
							cell: cell,
						});
					}
				}
			}
		});

		if (overlaps.length > 0) {
			return { hasOverlap: true, overlappingWords: overlaps };
		}

		return { hasOverlap: false };
	}
}
